// Import Libraries
import React, { useContext, useState } from 'react';
import {
  Button,
  Chip,
  Divider,
  ListItemIcon,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import AddIcon from '@mui/icons-material/Add';
import HistoryIcon from '@mui/icons-material/History';

// Import Components
import ScriptExport from '../../Modals/ScriptExport';

// Context
import ScriptContext from '../../../context/Script';
import ProjectViewContext from '../../../context/ProjectView';

// Import Services
import scriptService from '../../../services/scriptService';

const RECENT_COUNT = 3;

const STATUS_LABELS = {
  queued: 'Preparing',
  running: 'Building',
  processing: 'Building',
  failed: 'Failed',
};

const plural = (count, noun) => `${count} ${noun}${count === 1 ? '' : 's'}`;

// The original request (scenes + export options), which every job stores.
function parseRequest(row) {
  if (!row.request_json) return {};
  if (typeof row.request_json === 'object') return row.request_json;
  try {
    return JSON.parse(row.request_json) || {};
  } catch (e) {
    return {};
  }
}

// Export jobs have no name of their own, so describe them by what they hold.
// shot_count / includes_images were only recorded from the job-size migration
// on (add_export_job_size_fields.sql); older jobs have NULL / 0 there, so fall
// back to the scene count and image option from the stored request.
function exportTitle(row) {
  if (row.shot_count != null) {
    const shots = plural(row.shot_count, 'shot');
    return row.includes_images ? `${shots} · with images` : shots;
  }
  const { scenes, exportOptions = {} } = parseRequest(row);
  const size = Array.isArray(scenes) ? plural(scenes.length, 'scene') : 'Export';
  const withImages = !!exportOptions.includeImages && exportOptions.imageSize !== 'None';
  return withImages ? `${size} · with images` : size;
}

function exportSubtitle(row) {
  const status = STATUS_LABELS[String(row.status || '').toLowerCase()];
  return status ? `${row.created_at} · ${status}` : row.created_at;
}

// Construct Component
// The header's only contained button. Opens a menu with New export, the last
// few exports and View all exports; each opens the export modal on the
// matching section (Create New Export / Export History).
const ExportMenuButton = ({ iconOnly = false }) => {
  const { script } = useContext(ScriptContext);
  const { projectExports, setProjectExports } = useContext(ProjectViewContext);
  const [anchorEl, setAnchorEl] = useState(null);
  const [modal, setModal] = useState({ open: false, section: 'new', selectedExportId: null });
  const open = Boolean(anchorEl);

  const handleOpen = async (event) => {
    setAnchorEl(event.currentTarget);
    try {
      const data = await scriptService.listRequestedExports(script.id);
      if (data) setProjectExports(data);
    } catch (e) {
      console.error(e);
    }
  };

  const openModal = (section, selectedExportId = null) => {
    setAnchorEl(null);
    setModal({ open: true, section, selectedExportId });
  };

  const recent = (projectExports || []).slice(0, RECENT_COUNT);

  const button = (
    <Button
      variant="contained"
      color="primary"
      size={iconOnly ? 'small' : 'medium'}
      id="export-menu-button"
      aria-label={iconOnly ? 'Export' : undefined}
      aria-haspopup="menu"
      aria-controls={open ? 'export-menu' : undefined}
      aria-expanded={open}
      onClick={handleOpen}
      startIcon={iconOnly ? undefined : <FileUploadOutlinedIcon />}
      endIcon={
        <ArrowDropDownIcon
          sx={{
            transition: 'transform 150ms ease',
            transform: open ? 'rotate(180deg)' : 'none',
          }}
        />
      }
    >
      {iconOnly ? <FileUploadOutlinedIcon fontSize="small" /> : 'Export'}
    </Button>
  );

  return (
    <>
      {iconOnly ? <Tooltip title="Export">{button}</Tooltip> : button}

      <Menu
        id="export-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        MenuListProps={{ 'aria-labelledby': 'export-menu-button' }}
        PaperProps={{ sx: { width: 320, mt: 1 } }}
      >
        <MenuItem onClick={() => openModal('new')}>
          <ListItemIcon>
            <AddIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="New export" />
        </MenuItem>

        <Divider />
        <ListSubheader>RECENT EXPORTS</ListSubheader>

        {recent.length === 0 && (
          <MenuItem disabled>
            <ListItemText primary="No exports yet" />
          </MenuItem>
        )}

        {recent.map((row) => {
          const isCSV = String(row.type || '').toUpperCase() === 'CSV';
          return (
            <MenuItem key={row.id} onClick={() => openModal('history', row.id)}>
              <ListItemText primary={exportTitle(row)} secondary={exportSubtitle(row)} />
              <Chip label={isCSV ? 'CSV' : 'PDF'} variant="outlined" />
            </MenuItem>
          );
        })}

        <Divider />
        <MenuItem onClick={() => openModal('history')}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText primary="View all exports" />
        </MenuItem>
      </Menu>

      <ScriptExport
        modalIsOpen={modal.open}
        setModalIsOpen={(isOpen) => setModal((prev) => ({ ...prev, open: isOpen }))}
        section={modal.section}
        selectedExportId={modal.selectedExportId}
      />
    </>
  );
};

export default ExportMenuButton;
